import styled from '@emotion/styled';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { CustomTabStore } from '@seedlang/stores';
import { isBlank, isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import BackToTopButton from 'components/button/back_to_top_button';
import PageTitle from 'components/common/page_title';
import WorksheetBackButton from 'components/worksheet/breadcrumbs';
import WorksheetTabs from 'components/worksheet/worksheet_tabs';
import WorksheetInfo from 'components/worksheet/worksheet_info';
import { last, min } from 'lodash';
import { autorun, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

const Wrapper = styled.div`
  margin-top: 10px;
`;

const Navigation = styled.div`
  display: flex;
  align-items: ${props => props.alignItems};
  justify-content: space-between;
  flex-direction: ${props => props.flexDirection};
  margin-top: 5px;
  @media only screen and (max-width : 700px) {
    padding: 0px 10px;
  }
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  gap: ${props => props.gap};
  margin-top: ${props => props.marginTop};
  margin-bottom: ${props => props.marginBottom};
`;

@observer
class WorksheetShow extends React.Component {
  @observable showBackToTop = false;
  @observable worksheetBottom;
  @observable worksheetRight;
  @observable rerouted = false;

  constructor(props) {
    super(props);
    if (this.props.params.worksheetId) {
      this.getWorksheet();
      AppUI.createEvent('worksheets - view worksheet', {worksheet_id: this.props.params.worksheetId});
    } else if (this.props.params.exerciseId) {
      this.getExercise();
    }
    this.disposer = autorun(() => {
      if ((this.worksheetRoute || this.tabCount <= 1) && ExerciseUI.hasWorksheet && !this.rerouted) {
        this.routeToFirstTab();
      }
    });
  }

  componentDidMount() {
    if (!AppUI.layout.isMobile) {
      window.addEventListener('scroll', this.onScroll, true);
    }
  }

  componentWillUnmount() {
    ExerciseUI.worksheetStore.clearShowData();
    CustomTabStore.clearIndexData();
    this.disposer && this.disposer();
    if (!AppUI.layout.isMobile) {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  @computed get worksheetRoute() {
    return last(this.props.router.routes).name === `${ExerciseUI.worksheetNamespace}.show`;
  }

  @autobind routeToFirstTab() {
    this.rerouted = true;

    const worksheet = ExerciseUI.worksheet;
    if (!ExerciseUI.hasWorksheet && isBlank(worksheet)) return;

    // Determine the route name for the first tab
    let routeName = `${ExerciseUI.worksheetNamespace}.show`;
    if (AppUI.layout.isMobile) {
      routeName = `${ExerciseUI.worksheetNamespace}.info.show`;
    } else if (worksheet.exercises?.length > 0) {
      routeName = `${ExerciseUI.worksheetNamespace}.exercises.show`;
    } else if ((worksheet.worksheetWordsCount > 0 && worksheet.publishWords) || (isPresent(worksheet.unprocessedVocab) && worksheet.publishUnprocessedVocab)) {
      routeName = `${ExerciseUI.worksheetNamespace}.vocab.show`;
    } else if (worksheet.customTabs?.length > 0) {
      routeName = `${ExerciseUI.worksheetNamespace}.custom_tab.show`;
    } else if (worksheet.downloadLinks.length > 0) {
      routeName = `${ExerciseUI.worksheetNamespace}.download_links.index`;
    } else if (ExerciseUI.site.showWorksheetComments && !worksheet.forumThread?.hidden) {
      routeName = `${ExerciseUI.worksheetNamespace}.comments.show`;
    }

    // Build the route parameters
    const params = {worksheetId: worksheet.id};
    if (routeName.endsWith('custom_tab.show')) {
      params['customTabId'] = worksheet.customTabs[0]?.id;
    } else if (routeName.endsWith('exercises.show')) {
      params['exerciseId'] = worksheet.exercises[0]?.id;
    }

    // Use replace instead of push to avoid creating history entry for empty worksheet.show route
    this.props.router.replace({
      name: routeName,
      params: params,
    });
    AppUI.routeStore.setRouteName(routeName);
    AppUI.routeStore.params = params;
  }

  @autobind onScroll() {
    // I changed this to appear after 400 pixels of scrolling
    if (isPresent(document.getElementsByClassName('app')) && document.getElementsByClassName('app')[0]?.scrollTop > 400) {
      this.showBackToTop = true;
      const worksheetBody = document.getElementById('worksheet-body');
      if (isPresent(worksheetBody)) {
        this.worksheetRight = worksheetBody.getBoundingClientRect().right;
        this.worksheetBottom = worksheetBody.getBoundingClientRect().bottom;
      }
    } else if (this.showBackToTop) {
      this.showBackToTop = false;
    }
  }

  @autobind goBackToTop() {
    // document.getElementsByClassName('app')[0].scrollTo({top: 0, behavior: 'smooth'});
    AppUI.scrollToPageTop('smooth', AppUI.layout.isMobile ? 'video-player' : null);
  }

  @computed get worksheetId() {
    if (ExerciseUI.exerciseStore.hasShowData && !this.props.params.worksheetId) {
      return ExerciseUI.exerciseStore.showData.worksheetId;
    }
    return this.props.params.worksheetId;
  }

  @autobind getWorksheet() {
    ExerciseUI.set('worksheetId', this.worksheetId);
    ExerciseUI.getWorksheet(this.worksheetId, this.afterGetWorksheet);
  }

  @autobind afterGetWorksheet(resp) {
    ExerciseUI.worksheetStore.afterGetShow(resp);
  }

  @autobind getExercise() {
    ExerciseUI.exerciseStore.getShow({ids: {exerciseId: this.props.params.exerciseId}}, this.afterGetExercise);
  }

  @autobind afterGetExercise(resp) {
    ExerciseUI.exerciseStore.setShowData(resp);
    this.getWorksheet();
  }

  @computed get tabCount() {
    let res = 0;
    if (ExerciseUI.worksheet?.hasPublishedVocab) {
      res += 1;
    }
    if (ExerciseUI.hasWorksheet && ExerciseUI.worksheet?.exercises.length > 0) {
      res += 1;
    }
    if (ExerciseUI.hasWorksheet && isPresent(ExerciseUI.worksheet.customTabs)) {
      res += ExerciseUI.worksheet.customTabs.length;
    }
    if (ExerciseUI.hasWorksheet && isPresent(ExerciseUI.worksheet.downloadLinks)) {
      res += 1;
    }
    if (ExerciseUI.site.showWorksheetComments && ExerciseUI.hasWorksheet && ExerciseUI.worksheet.hasForumThread && !ExerciseUI.worksheet.forumThread.hidden && (ExerciseUI.hasCurrentWorksheetAccess || ExerciseUI.worksheet.forumThread?.commentsCount > 0)) {
      res += 1;
    }
    return res;
  }

  @computed get tabSectionBorderRadius() {
    if (this.tabCount > 1 && !AppUI.layout.isMobile) {
      return '0 10px 10px 10px';
    }
    return this.standaloneSectionBorderRadius;
  }

  @computed get standaloneSectionBorderRadius() {
    if (ExerciseUI.layout.isMobile) {
      return '0';
    }
    return '10px';
  }

  render() {
    return (
      <Wrapper id='worksheet-body'>
        <PageTitle>{ExerciseUI.worksheet?.nameWithoutEpisodeCode}</PageTitle>
        {
          this.showBackToTop && AppUI.routeStore.routeName === 'worksheets.vocab.show' &&
            <BackToTopButton
              floating
              background={AppUI.site.accentColor}
              left={AppUI.layout.isMobile ? null : min([AppUI.layout.viewportWidth - 50, this.worksheetRight - 40])}
              top={AppUI.layout.isMobile ? null : ExerciseUI.siteIsDefault ? min([AppUI.layout.viewportHeight - 100, this.worksheetBottom - 55]) : min([AppUI.layout.viewportHeight - 100, this.worksheetBottom - 61])}
              right={AppUI.layout.isMobile ? '10px' : null}
              bottom={AppUI.layout.isMobile ? '50px' : null}
              onClick={this.goBackToTop}
            />
        }
        {
          ExerciseUI.layout.isDesktop &&
            <WorksheetInfo
              params={this.props.params}
              borderRadius={this.standaloneSectionBorderRadius}
            />
        }
        <Navigation
          id='page-top'
          flexDirection={AppUI.layout.isMobile ? 'column' : 'row'}
          alignItems={AppUI.layout.isMobile ? 'flex-start' : 'center'}
        >
          {
            this.tabCount <= 1 &&
              <Tabs
                marginTop={AppUI.layout.isMobile ? '0px' : '20px'}
                marginBottom={AppUI.layout.isMobile ? '10px' : '0px'}
              />
          }
          {
            AppUI.layout.isMobile &&
              <WorksheetBackButton
                showAsBreadcrumbs
                currentRouteName={last(this.props.router.routes).name}
              />
          }
          {
            this.tabCount > 1 &&
              <Tabs
                role='tablist'
                aria-label='Content tabs'
                gap={AppUI.layout.isMobile ? '5px' : '10px'}
                marginTop={AppUI.layout.isMobile ? '0px' : '10px'}
                marginBottom={AppUI.layout.isMobile ? '10px' : '0px'}
              >
                <WorksheetTabs
                  worksheetId={this.worksheetId}
                  params={this.props.params}
                  small={AppUI.layout.isMobile}
                />
              </Tabs>
          }
        </Navigation>
        {this.props.children && React.cloneElement(this.props.children, {
          borderRadius: this.tabSectionBorderRadius,
          standaloneSectionBorderRadius: this.standaloneSectionBorderRadius,
          tabCount: this.tabCount,
        })}
      </Wrapper>
    );
  }
}

export default WorksheetShow;
